export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function buildQueryPaginatedUrl(
  searchUrl: string,
  page: number,
  paramName = "page",
): string {
  const url = new URL(searchUrl);
  if (page > 1) {
    url.searchParams.set(paramName, page.toString());
  } else {
    url.searchParams.delete(paramName);
  }
  return url.toString();
}

export function replacePathPageSegment(searchUrl: string, page: number): string {
  return searchUrl.replace(/\/list-\d+/, `/list-${page}`);
}

export function parsePrice(priceStr?: string | null): number | null {
  if (!priceStr) return null;
  const cleaned = priceStr.replace(/[^\d.]/g, "");
  if (!cleaned) return null;
  const value = Number.parseFloat(cleaned);
  return Number.isNaN(value) ? null : value;
}

/**
 * Smallest figure accepted as a residential sale price. Below it the amount is a rent, a
 * placeholder ($1,001) or a typo ($4,000 for an Albert Park house), never a sale.
 */
export const MIN_PLAUSIBLE_SALE_PRICE = 10_000;

const RENT_TEXT_RE =
  /\b(?:rent(?:ed|al|s)?|leased?|tenanted|weekly|monthly|per\s+(?:week|month|annum|calendar\s+month))\b|\/\s*(?:wk|week|mth|month|calendar\s+month|annum)\b|(?:\b|\d)(?:p\.?\s?w|p\.?\s?c\.?\s?m)\b/i;
const GUIDE_TEXT_RE =
  /\b(?:offers?|over|above|from|guide|range|between|starting|circa|approx(?:imately)?|estimated?|eoi|expressions?)\b|\+|\d\s*[mk]?\s*(?:-|–|—|\bto\b)\s*\$?\s*\d/i;
const AMOUNT_RE = /\$\s*(\d[\d,]*)(\.\d+)?\s*(m(?:il(?:lion)?)?|k)?(?![a-z\d])/gi;

/**
 * One sale price from display text: "$1,885,000", "$1.2m", "$950k", "Sold for $1,250,000".
 * Returns null rather than a wrong number for text with no amount ("Contact agent", "Price
 * withheld"), a rent ("$1484.00 / calendar month", "$540 per week", "$600pw"), a range or guide
 * ("$1.2m - $1.4m", "Offers over $900k"), more than one amount, or malformed thousands separators.
 */
export function parseSalePriceText(text: unknown): number | null {
  if (typeof text !== "string" || RENT_TEXT_RE.test(text) || GUIDE_TEXT_RE.test(text)) return null;
  const amounts = [...text.matchAll(AMOUNT_RE)];
  if (amounts.length !== 1) return null;
  const [, whole, fraction = "", unit = ""] = amounts[0];
  if (whole.includes(",") && !/^\d{1,3}(?:,\d{3})+$/.test(whole)) return null;
  const multiplier = /^m/i.test(unit) ? 1_000_000 : /^k$/i.test(unit) ? 1_000 : 1;
  // Without a unit the fraction is cents; with one it scales ("$1.25m").
  if (multiplier === 1 && fraction.length > 3) return null;
  const digits = `${whole.replace(/,/g, "")}${multiplier === 1 ? "" : fraction}`;
  const value = Math.round(Number(digits) * multiplier);
  return Number.isFinite(value) && value > 0 ? value : null;
}

/** Parse-warning slug, named after the poisoned-data detection method for the same defect. */
export const IMPLAUSIBLE_SALE_PRICE_WARNING = "implausible_sale_price";

/**
 * Null a salePrice below MIN_PLAUSIBLE_SALE_PRICE. The figure is kept as salePriceRejected with an
 * implausible_sale_price entry in parseWarnings (the parse_warnings convention of the SQM ingress),
 * both of which land in property_snapshots.raw_data. Idempotent.
 */
export function rejectImplausibleSalePrice(data: Record<string, unknown>): Record<string, unknown> {
  const value = data.salePrice;
  if (typeof value !== "number" || value >= MIN_PLAUSIBLE_SALE_PRICE) return data;
  data.salePrice = null;
  if (value > 0) {
    data.salePriceRejected = value;
    const warnings = Array.isArray(data.parseWarnings) ? data.parseWarnings : [];
    if (!warnings.includes(IMPLAUSIBLE_SALE_PRICE_WARNING)) {
      data.parseWarnings = [...warnings, IMPLAUSIBLE_SALE_PRICE_WARNING];
    }
  }
  return data;
}

export function parseNumber(value?: string | null): number | null {
  if (!value) return null;
  const match = value.match(/\d+/);
  if (!match) return null;
  const parsed = Number.parseInt(match[0], 10);
  return Number.isNaN(parsed) ? null : parsed;
}

const AU_STATES = /^(VIC|NSW|QLD|SA|WA|TAS|ACT|NT)$/i;
const POSTCODE_RE = /^\d{4}$/;
// Domain page title pattern: "Sold 4 Landridge St, Glen Waverley VIC 3150 on
// 30 Oct 2025 - 2020260842 | Domain". Strip the leading "Sold " (or
// "For Sale ") verb, the trailing listing-id suffix, and the " | Domain"
// site name so we end up with just the street + suburb portion.
const DOMAIN_TITLE_RE = /^(?:Sold|For Sale|For Rent|Auction|Leased)\s+(.+?)\s+on\s+\d{1,2}\s+\w+\s+\d{4}\s*-\s*\d+\s*(?:\|\s*[\w.]+)?$/i;

function stripDomainPageTitle(address: string): string {
  const match = address.match(DOMAIN_TITLE_RE);
  return match ? match[1].trim() : address;
}

function extractSaleDateFromTitle(address: string): string | null {
  const match = address.match(/\s+on\s+(\d{1,2}\s+\w+\s+\d{4})\s*-/i);
  return match ? match[1] : null;
}

const LISTING_TYPE_SPELLINGS: Record<string, "sold" | "buy" | "rent" | null> = {
  sold: "sold",
  buy: "buy",
  sale: "buy",
  sell: "buy",
  "for sale": "buy",
  forsale: "buy",
  auction: "buy",
  "under offer": "buy",
  underoffer: "buy",
  "under contract": "buy",
  undercontract: "buy",
  "private sale": "buy",
  "expressions of interest": "buy",
  "new development": "buy",
  "off the plan": "buy",
  rent: "rent",
  "for rent": "rent",
  lease: "rent",
  leased: "rent",
  "access denied": null,
  deny: null,
  unknown: null,
};

/**
 * One of the app's listing types (sold, buy, rent) for any spelling a site or parser produced:
 * "For Sale", "under_offer", "underOffer", "auction" and "new apartments / off the plan" are all
 * for-sale listings (buy). Bot-page junk ("access denied") is null; an unrecognised value is
 * returned unchanged so it is never mistaken for a for-sale listing.
 */
export function canonicalListingType(value: unknown): string | null {
  if (typeof value !== "string" || !value.trim()) return null;
  const key = value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[_\-·/]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (key in LISTING_TYPE_SPELLINGS) return LISTING_TYPE_SPELLINGS[key];
  if (key.startsWith("auction")) return "buy";
  for (const [spelling, type] of Object.entries(LISTING_TYPE_SPELLINGS)) {
    if (type === "buy" && key.includes(spelling) && spelling.includes(" ")) return type;
  }
  return value;
}

/**
 * Normalize the extracted-data payload from any provider before it is written
 * to property_snapshots. Idempotent + cross-runtime (Node + Deno).
 *
 * Fixes three recurring extractor failure modes seen in production:
 * 1. priceDisplay populated but salePrice null — parse the $ amount; a salePrice below
 *    MIN_PLAUSIBLE_SALE_PRICE is nulled and kept as salePriceRejected.
 * 2. Domain page-title address ("Sold X St, Suburb VIC 3000 on 30 Oct 2025 - 12345 | Domain")
 *    leaking into the address field — strip prefix/suffix; opportunistically
 *    recover saleDate from the title.
 * 3. Comma-delimited address but null suburb/state/postcode — split and fill.
 *
 * Mutates and returns the same object so callers can chain or treat as a no-op.
 */
export function normalizeExtractedData(
  data: Record<string, unknown>,
): Record<string, unknown> {
  // 0. A sale price and date come only from a sold listing. A for-sale or rental page's figure
  //    is an asking price (price), never a sale: a Domain BUY page's datePublished and an REA
  //    buy page's price were landing in sale_date/sale_price (esadmin-codex review, 2026-10-15).
  if (data.listingType !== undefined) data.listingType = canonicalListingType(data.listingType);
  const notSold = data.listingType === "buy" || data.listingType === "rent";
  if (notSold) {
    const asking =
      typeof data.salePrice === "number" ? data.salePrice : parseSalePriceText(data.priceDisplay);
    if ((typeof data.price !== "number" || data.price === 0) && asking !== null)
      data.price = asking;
  }

  // 1. priceDisplay → salePrice fallback. parseSalePriceText refuses ranges, guides and rents,
  //    so a display a site parser deliberately left without a price stays without one.
  if (!notSold && (typeof data.salePrice !== "number" || data.salePrice === 0)) {
    const parsed = parseSalePriceText(data.priceDisplay);
    if (parsed !== null) {
      data.salePrice = parsed;
      if (typeof data.price !== "number" || data.price === 0) data.price = parsed;
    }
  }
  // Whatever supplied it, a figure below the floor is not a sale price. upsert_property_from_queue
  // copies price into sale_price when a sold listing has none, so price gets the same floor there.
  rejectImplausibleSalePrice(data);
  if (
    data.listingType === "sold" &&
    typeof data.price === "number" &&
    data.price < MIN_PLAUSIBLE_SALE_PRICE
  ) {
    data.price = null;
  }

  // 2. Strip Domain page-title noise from address and opportunistically
  //    recover sale_date when the LLM only emitted the page title.
  if (typeof data.address === "string") {
    const recoveredDate = extractSaleDateFromTitle(data.address);
    if (recoveredDate && (typeof data.saleDate !== "string" || data.saleDate.length === 0)) {
      data.saleDate = recoveredDate;
    }
    data.address = stripDomainPageTitle(data.address).trim();
  }
  if (notSold) {
    data.salePrice = null;
    data.saleDate = null;
  }

  // 3. Backfill suburb/state/postcode from comma-delimited address.
  if (typeof data.address === "string") {
    const parts = data.address.split(",").map((s) => s.trim()).filter(Boolean);
    if (parts.length >= 2) {
      const tail = parts[parts.length - 1];
      // Domain compact format: "<suburb> <state> <postcode>" in a single segment.
      const suburbStatePostcode = tail.match(/^(.+?)\s+(VIC|NSW|QLD|SA|WA|TAS|ACT|NT)\s+(\d{4})$/i);
      // REA/Domain expanded format: state + postcode as their own trailing segments.
      const stateAndPostcode = tail.match(/^(VIC|NSW|QLD|SA|WA|TAS|ACT|NT)\s+(\d{4})$/i);
      if (suburbStatePostcode) {
        if (!data.suburb) data.suburb = suburbStatePostcode[1].trim();
        if (!data.state) data.state = suburbStatePostcode[2].toUpperCase();
        if (!data.postcode) data.postcode = suburbStatePostcode[3];
      } else if (stateAndPostcode) {
        if (!data.state) data.state = stateAndPostcode[1].toUpperCase();
        if (!data.postcode) data.postcode = stateAndPostcode[2];
        const suburbCandidate = parts[parts.length - 2];
        if (!data.suburb && suburbCandidate && !/^\d/.test(suburbCandidate)) {
          data.suburb = suburbCandidate;
        }
      } else if (parts.length >= 3) {
        const maybePostcode = parts[parts.length - 1];
        const maybeState = parts[parts.length - 2];
        const maybeSuburb = parts[parts.length - 3];
        if (!data.postcode && POSTCODE_RE.test(maybePostcode)) data.postcode = maybePostcode;
        if (!data.state && AU_STATES.test(maybeState)) data.state = maybeState.toUpperCase();
        if (!data.suburb && maybeSuburb.length > 0 && !/^\d/.test(maybeSuburb)) {
          data.suburb = maybeSuburb;
        }
      }
    }
  }

  return data;
}
